// BUILD-042 (Oct 18) - HR Administration -> People -> Pending work.
// The company-wide list behind the red HR count: every assigned document still open (waiting on the EMPLOYEE, or waiting for HR REVIEW) and every
// open checklist task HR may track. It is a view over existing records - no new task or notification system - and it reads
// GET /hr/admin/pending-work, which applies the same two existing authorities as the count (document management / company-scope checklist
// assignment) and returns totals that are exact across pages. Filter by employee and by state; paged on the server.
//
// A failed read is shown as a plain "could not load" message with a retry - never as an empty list that looks like "nothing pending".
// Only the stored task due date is shown. Documents have no due date or expiry in the data, and no overdue rule exists for tasks, so no
// overdue / expiry category is offered here (nothing is invented and nothing completed is reopened).
import { useCallback, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getHrAdminPendingWork } from '../../api/client';
import { useAdminAttention } from './attentionStore';
import { SectionHeading } from './workspace/SectionHeading';
import { adminItemPath, adminStateLabel, pendingBreakdownText, type PendingStateFilter, type PendingWorkPage } from './workspace/attentionModel';
import { formatLocalDate } from './hrFormat';

const PAGE_SIZE = 25;
const STATE_OPTIONS: Array<{ value: PendingStateFilter; label: string }> = [
  { value: 'all', label: 'All open work' },
  { value: 'awaiting_employee', label: 'Waiting on employee' },
  { value: 'needs_hr_review', label: 'Needs HR review' },
  { value: 'tasks', label: 'Open checklist tasks' },
  { value: 'corrections', label: 'Correction requests' },
];

function chipClass(kind: 'review' | 'awaiting_employee' | 'task' | 'correction'): string {
  if (kind === 'review') return 'text-amber-900 bg-amber-100 border border-amber-300';
  if (kind === 'awaiting_employee') return 'text-sky-900 bg-sky-50 border border-sky-200';
  if (kind === 'correction') return 'text-purple-900 bg-purple-50 border border-purple-200';
  return 'text-gray-700 bg-gray-100 border border-gray-200';
}

/** `reviewerView` (Oct 19 06:28): the lite reviewer workspace for someone who reviews profile corrections but is not an HR administrator.
 *  Same list, same server route; it only lists correction requests (state fixed) and words the page for that audience. */
export function PendingWorkPanel({ reviewerView = false, embedded = false }: { reviewerView?: boolean; embedded?: boolean } = {}) {
  const navigate = useNavigate();
  const attention = useAdminAttention();
  const employeeSelectId = useId();
  const stateSelectId = useId();
  const [employeeId, setEmployeeId] = useState('');
  const [state, setState] = useState<PendingStateFilter>(reviewerView ? 'corrections' : 'all');
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState<PendingWorkPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Employees seen so far (kept across filter changes so choosing one person does not make the other options disappear).
  const [employeeOptions, setEmployeeOptions] = useState<Array<{ employeeId: string; employeeName: string | null; count: number }>>([]);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    getHrAdminPendingWork({ employeeId: employeeId || undefined, state, offset, limit: PAGE_SIZE })
      .then((p) => {
        if (cancelled) return;
        setPage(p); setError(null);
        setEmployeeOptions(p.employees ?? []);
        // A filter or page that fell off the end (work was completed meanwhile) goes back to the last real page, never to a blank one.
        if (p.total > 0 && offset >= p.total) setOffset(Math.max(0, Math.floor((p.total - 1) / PAGE_SIZE) * PAGE_SIZE));
      })
      .catch((e: any) => { if (!cancelled) setError(e?.message || 'Could not load pending work.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [employeeId, state, offset]);

  // Reload on any filter/page change AND whenever the shared attention counts are re-read (every successful /hr write refreshes them),
  // so the list follows assign / sign / review / cancel without a manual reload.
  useEffect(() => load(), [load, attention.data]);

  const items = page?.items ?? [];
  const from = page && page.total > 0 ? page.offset + 1 : 0;
  const to = page ? page.offset + items.length : 0;
  const nothingAtAll = page && page.authorized && (reviewerView ? page.counts.corrections === 0 : page.counts.total === 0);

  return (
    <div data-testid="pending-work-panel">
      {/* embedded: rendered inside the Employees page's "Open work" tab, which already has the page heading (Oct 24). */}
      {!embedded && <SectionHeading title="Pending work" subtitle={reviewerView ? 'Profile correction requests still waiting for your review.' : 'Everything still open: documents waiting on employees, documents waiting for your review, and open checklist tasks.'} />}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5 min-w-0">
        {error ? (
          <div role="alert" className="flex items-center justify-between gap-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="pending-work-error">
            <span>We could not load the pending work just now. This is not the same as nothing being pending.</span>
            <button type="button" onClick={() => load()} className="text-xs underline flex-shrink-0">Try again</button>
          </div>
        ) : !page ? (
          <div className="text-sm text-gray-400">Loading…</div>
        ) : !page.authorized ? (
          <div className="text-sm text-gray-500" data-testid="pending-work-unauthorized">Your access does not include HR pending work.</div>
        ) : (
          <>
            <div className="text-sm text-gray-700 mb-3" data-testid="pending-work-summary">
              {reviewerView
                ? <><span className="font-semibold">{page.counts.corrections} open</span> · correction {page.counts.corrections === 1 ? 'request' : 'requests'} waiting for your review</>
                : <><span className="font-semibold">{page.counts.total} open</span> · {pendingBreakdownText(page.counts)}</>}
            </div>
            <div className="flex flex-wrap items-end gap-3 mb-3">
              <div className="flex flex-col gap-1 min-w-0 w-full sm:w-auto">
                <label htmlFor={employeeSelectId} className="text-xs text-gray-500">Employee</label>
                <select id={employeeSelectId} value={employeeId} onChange={(e) => { setEmployeeId(e.target.value); setOffset(0); }} className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full sm:w-auto sm:min-w-[14rem]" data-testid="pending-filter-employee">
                  <option value="">All employees</option>
                  {employeeOptions.map((o) => <option key={o.employeeId} value={o.employeeId}>{(o.employeeName ?? 'Employee')} ({o.count})</option>)}
                  {employeeId && !employeeOptions.some((o) => o.employeeId === employeeId) && <option value={employeeId}>Selected employee</option>}
                </select>
              </div>
              {!reviewerView && <div className="flex flex-col gap-1 min-w-0 w-full sm:w-auto">
                <label htmlFor={stateSelectId} className="text-xs text-gray-500">Show</label>
                <select id={stateSelectId} value={state} onChange={(e) => { setState(e.target.value as PendingStateFilter); setOffset(0); }} className="border border-gray-300 rounded px-2 py-1.5 text-sm w-full" data-testid="pending-filter-state">
                  {STATE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>}
              {(employeeId || (!reviewerView && state !== 'all')) && (
                <button type="button" onClick={() => { setEmployeeId(''); setState(reviewerView ? 'corrections' : 'all'); setOffset(0); }} className="text-xs text-[#176A9B] hover:underline pb-2" data-testid="pending-filter-clear">Clear filters</button>
              )}
              {loading && <span className="text-xs text-gray-400 pb-2" aria-live="polite">Updating…</span>}
            </div>

            {nothingAtAll ? (
              <div className="text-sm text-gray-500 py-3" data-testid="pending-work-empty">{reviewerView ? 'Nothing is waiting for your review right now.' : 'Nothing is pending right now: no document is waiting on an employee or on HR review, and no checklist task is open.'}</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-gray-500 py-3" data-testid="pending-work-no-match">Nothing matches these filters.</div>
            ) : (
              <ul className="divide-y divide-gray-100" data-testid="pending-work-list">
                {items.map((i) => (
                  <li key={i.itemId} className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1.5 py-2.5" data-testid="pending-work-row" data-state={i.state} data-employee-id={i.employeeId}>
                    <div className="min-w-0 flex-1 basis-48">
                      <div className="text-sm text-gray-900 break-words"><span className="font-semibold">{i.employeeName ?? 'Employee'}</span> · {i.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{i.detail}{i.dueDate ? ` · due ${formatLocalDate(i.dueDate)}` : ''}</div>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 min-w-0">
                      <span className={`text-xs font-medium px-2 py-1 rounded-md ${chipClass(i.kind)}`}>{adminStateLabel(i)}</span>
                      <button type="button" onClick={() => navigate(adminItemPath(i))} className="text-xs text-[#176A9B] hover:underline" aria-label={`Open ${i.title} for ${i.employeeName ?? 'this employee'}`} data-testid="pending-work-open">Open →</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {page.total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-2 mt-3 text-xs text-gray-600" data-testid="pending-work-pager">
                <span>Showing {from}–{to} of {page.total}</span>
                <span className="flex gap-2">
                  <button type="button" disabled={page.offset <= 0} onClick={() => setOffset(Math.max(0, page.offset - PAGE_SIZE))} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40" data-testid="pending-prev">Previous</button>
                  <button type="button" disabled={page.offset + items.length >= page.total} onClick={() => setOffset(page.offset + PAGE_SIZE)} className="px-2 py-1 rounded border border-gray-300 disabled:opacity-40" data-testid="pending-next">Next</button>
                </span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
